import { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { Tooltip } from 'react-tooltip';
import Plotly from 'plotly.js-dist';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import './HealthDataVisualizer.css';

const getSelectionDisplayText = (selected, options) => {
  if (selected.length === 0) return 'None Selected';
  if (selected.length === options.length) return 'All Selected';
  if (selected.length === 1) return selected[0];
  return `${selected.length} Selected`;
};

const SearchableSingleSelect = ({ options, selected, onSelect, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={selected || placeholder} onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
          className="dropdown-search-input" />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? filteredOptions.map((opt, index) => (
            <div key={index} className={`dropdown-item ${selected === opt ? 'selected' : ''}`}
              onClick={() => { onSelect(opt); setIsOpen(false); setSearchTerm(''); }}>
              <input type="radio" checked={selected === opt} readOnly />
              <span>{opt}</span>
            </div>
          )) : <div className="dropdown-item no-options">No options found</div>}
        </div>
      )}
    </div>
  );
};

const SearchableMultiSelect = ({ options, selected, onToggle, onSelectAll, onDeselectAll, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)}>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={getSelectionDisplayText(selected, options)} onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
          className="dropdown-search-input" />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-actions">
            <button className="dropdown-action-button" onClick={onSelectAll}>Select All</button>
            <button className="dropdown-action-button" onClick={onDeselectAll}>Deselect All</button>
          </div>
          {filteredOptions.length > 0 ? filteredOptions.map((opt, index) => (
            <div key={index} className={`dropdown-item ${selected.includes(opt) ? 'selected' : ''}`} onClick={() => onToggle(opt)}>
              <input type="checkbox" checked={selected.includes(opt)} readOnly />
              <span>{opt}</span>
            </div>
          )) : <div className="dropdown-item no-options">No options found</div>}
        </div>
      )}
    </div>
  );
};

const HealthDataVisualizer = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dataStructure, setDataStructure] = useState({ countries: [], indicators: [], years: [] });
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const [showConfidenceBands, setShowConfidenceBands] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showData, setShowData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const chartRef = useRef(null);
  const contentAreaRef = useRef(null);

  const colorPalette = ['#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED', '#DB2777', '#0891B2', '#EA580C', '#4F46E5', '#65A30D'];

  const openMetadataPDF = () => window.open('/Current/CDIC_TiD_Metadata.pdf', '_blank');

  const exportToPNG = async () => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;
    try {
      const canvas = await html2canvas(contentArea, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.font = 'italic 22px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`© Health Systems Innovation Lab, Harvard University, ${new Date().getFullYear()}`, 40, canvas.height - 30);
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoHeight = 50;
        const logoWidth = logoHeight * (logo.width / logo.height);
        ctx.drawImage(logo, finalCanvas.width - logoWidth - 40, canvas.height - logoHeight - 20, logoWidth, logoHeight);
        finalCanvas.toBlob(blob => {
          const countriesStr = selectedCountries.length <= 3 ? selectedCountries.join('_').replace(/\s+/g, '-') : `${selectedCountries.length}_countries`;
          saveAs(blob, `T1DM_incidence_${countriesStr}_${new Date().toISOString().split('T')[0]}.png`);
        });
      };
      logo.onerror = () => {
        finalCanvas.toBlob(blob => {
          const countriesStr = selectedCountries.length <= 3 ? selectedCountries.join('_').replace(/\s+/g, '-') : `${selectedCountries.length}_countries`;
          saveAs(blob, `T1DM_incidence_${countriesStr}_${new Date().toISOString().split('T')[0]}.png`);
        });
      };
      logo.src = '/logo.png';
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export image.');
    }
  };

  // Load CSV data with RANDOM initial selection
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const csvResponse = await fetch('/Current/GC-CDIC_TiD_incidence_dataset.csv');
        if (!csvResponse.ok) throw new Error(`HTTP error: ${csvResponse.status}`);
        const csvText = await csvResponse.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          complete: results => {
            const cleanedData = results.data
              .filter(row => row.Country && row.Indicator && row.Year)
              .map(row => ({
                Country: row.Country.trim(),
                ISOCode: row.ISOCode ? row.ISOCode.trim() : '',
                Indicator: row.Indicator.trim(),
                Year: parseInt(row.Year),
                UpperBound: parseFloat(row['Upper Bound Value']),
                Mean: parseFloat(row['Mean Value']),
                LowerBound: parseFloat(row['Lower Bound Value'])
              }))
              .filter(row => !isNaN(row.Year) && !isNaN(row.Mean));

            const structure = {
              countries: [...new Set(cleanedData.map(row => row.Country))].sort(),
              indicators: [...new Set(cleanedData.map(row => row.Indicator))].sort(),
              years: [...new Set(cleanedData.map(row => row.Year))].sort((a, b) => a - b)
            };

            // RANDOM INITIAL SELECTION
            const randomCountry = structure.countries.length > 0 
              ? structure.countries[Math.floor(Math.random() * structure.countries.length)] 
              : null;
            const randomIndicator = structure.indicators.length > 0 
              ? structure.indicators[Math.floor(Math.random() * structure.indicators.length)] 
              : null;

            // Set all state together
            setData(cleanedData);
            setDataStructure(structure);
            setSelectedCountries(randomCountry ? [randomCountry] : []);
            setSelectedIndicator(randomIndicator);
            setLoading(false);
          },
          error: error => {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter data
  useEffect(() => {
    if (data.length === 0 || selectedCountries.length === 0 || !selectedIndicator) {
      setFilteredData([]);
      return;
    }
    const filtered = data.filter(row => selectedCountries.includes(row.Country) && row.Indicator === selectedIndicator);
    filtered.sort((a, b) => a.Country !== b.Country ? a.Country.localeCompare(b.Country) : a.Year - b.Year);
    setFilteredData(filtered);
  }, [data, selectedCountries, selectedIndicator]);

  // Update chart
  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) {
      if (chartRef.current) Plotly.purge(chartRef.current);
      return;
    }
    renderLineChart();
  }, [filteredData, showLegend, showConfidenceBands]);

  const renderLineChart = () => {
    const traces = [];
    const countries = [...new Set(filteredData.map(row => row.Country))].sort();

    countries.forEach((country, idx) => {
      const countryData = filteredData.filter(row => row.Country === country).sort((a, b) => a.Year - b.Year);
      if (countryData.length === 0) return;

      const years = countryData.map(row => row.Year);
      const means = countryData.map(row => row.Mean);
      const upperBounds = countryData.map(row => row.UpperBound);
      const lowerBounds = countryData.map(row => row.LowerBound);
      const color = colorPalette[idx % colorPalette.length];

      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 0, g: 0, b: 0 };
      };
      const rgb = hexToRgb(color);

      if (showConfidenceBands) {
        traces.push({ x: years, y: upperBounds, type: 'scatter', mode: 'lines', name: `${country} (Upper)`,
          line: { color: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`, width: 1, dash: 'dot' }, showlegend: false, hoverinfo: 'skip' });
        traces.push({ x: years, y: lowerBounds, type: 'scatter', mode: 'lines', name: `${country} (Lower)`,
          line: { color: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`, width: 1, dash: 'dot' },
          fill: 'tonexty', fillcolor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`, showlegend: false, hoverinfo: 'skip' });
      }

      traces.push({
        x: years, y: means, type: 'scatter', mode: 'lines+markers', name: country,
        line: { color, width: 2.5, shape: 'spline' },
        marker: { color, size: 4, line: { color: 'white', width: 1 } },
        hovertemplate: showConfidenceBands
          ? `<b>${country}</b><br>Year: %{x}<br>Mean: %{y:.2f}<br>Upper: %{customdata[0]:.2f}<br>Lower: %{customdata[1]:.2f}<extra></extra>`
          : `<b>${country}</b><br>Year: %{x}<br>Value: %{y:.2f}<extra></extra>`,
        customdata: countryData.map(row => [row.UpperBound, row.LowerBound])
      });
    });

    const allYValues = filteredData.flatMap(row => showConfidenceBands ? [row.Mean, row.UpperBound, row.LowerBound] : [row.Mean]).filter(v => !isNaN(v));
    const yMin = Math.min(...allYValues);
    const yMax = Math.max(...allYValues);
    const yPadding = (yMax - yMin) * 0.1;

    const shortIndicator = selectedIndicator.length > 80 ? selectedIndicator.substring(0, 77) + '...' : selectedIndicator;

    const layout = {
      title: { text: `<b>${shortIndicator}</b><br><sub>${selectedCountries.length === 1 ? selectedCountries[0] : `${selectedCountries.length} Countries`}${showConfidenceBands ? ' — with 95% Confidence Intervals' : ''}</sub>`, font: { size: 24 } },
      xaxis: { title: 'Year', tickmode: 'linear', dtick: 5, gridcolor: '#E5E7EB', showgrid: true },
      yaxis: {
        title: selectedIndicator.includes('rate') ? 'Rate (per 100,000)' : selectedIndicator.includes('percentage') ? 'Percentage (%)' : selectedIndicator.includes('number') ? 'Number of Cases' : 'Value',
        showgrid: true, gridcolor: '#E5E7EB', zeroline: true, zerolinecolor: '#9CA3AF',
        range: [Math.max(0, yMin - yPadding), yMax + yPadding]
      },
      showlegend: showLegend && selectedCountries.length > 1,
      legend: { x: 1.02, y: 1, xanchor: 'left', bgcolor: 'rgba(255,255,255,0.9)', bordercolor: '#D1D5DB', borderwidth: 1 },
      hovermode: 'x unified',
      plot_bgcolor: 'white', paper_bgcolor: 'white',
      margin: { l: 80, r: 150, t: 100, b: 80 }
    };

    Plotly.newPlot(chartRef.current, traces, layout, { responsive: true, displayModeBar: true, modeBarButtonsToRemove: ['select2d', 'lasso2d', 'toImage'], displaylogo: false });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const toggleData = () => setShowData(!showData);
  const handleCountryToggle = (country) => setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);

  let tableData = [...filteredData];
  if (sortConfig.key) {
    tableData.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  if (loading) return <div className="loading-container"><div className="loading-message">Loading T1DM Incidence Data...</div></div>;

  const hasAllSelections = selectedCountries.length > 0 && selectedIndicator;

  return (
    <div className="explorer-container" data-sidebar-open={isSidebarOpen.toString()} style={{ '--sidebar-width': isSidebarOpen ? '300px' : '40px' }}>
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
        {isSidebarOpen && (
          <div className="sidebar-content">
            <div className="section">
              <h2 className="section-title">Countries</h2>
              <SearchableMultiSelect options={dataStructure.countries} selected={selectedCountries} onToggle={handleCountryToggle}
                onSelectAll={() => setSelectedCountries(dataStructure.countries.slice(0, 10))} onDeselectAll={() => setSelectedCountries([])}
                placeholder="Select Countries" id="countries-select" />
              <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '4px' }}>{selectedCountries.length} of {dataStructure.countries.length} selected</p>
            </div>

            <div className="section">
              <h2 className="section-title">Indicator</h2>
              <SearchableSingleSelect options={dataStructure.indicators} selected={selectedIndicator} onSelect={setSelectedIndicator} placeholder="Select Indicator" id="indicators-select" />
            </div>

            <div className="section">
              <label className="checkbox-label"><input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} /><span>Show Legend</span></label>
            </div>

            <div className="section">
              <label className="checkbox-label"><input type="checkbox" checked={showConfidenceBands} onChange={(e) => setShowConfidenceBands(e.target.checked)} /><span>Show Confidence Intervals</span></label>
            </div>

            <div className="section">
              <button className="button button-secondary" onClick={toggleData} style={{ width: '100%' }}>{showData ? 'Hide Data Table' : 'Show Data Table'}</button>
            </div>

            <div className="section">
              <button className="button button-secondary" onClick={openMetadataPDF} style={{ width: '100%' }}>Show Metadata</button>
            </div>
          </div>
        )}
      </div>

      <div className="main-content">
        {hasAllSelections && (
            <button onClick={exportToPNG} title="Download chart as PNG"
              style={{ position: 'fixed', top: 70, right: 24, zIndex: 100, display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 24px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', color: '#495057', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              PNG
            </button>
          )}

        <div className="content-area" ref={contentAreaRef}>
          {!hasAllSelections ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280', fontSize: '1.1rem' }}>
              <p style={{ marginBottom: '1rem' }}>Please select options to display the chart:</p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ color: selectedCountries.length > 0 ? '#10B981' : '#EF4444' }}>{selectedCountries.length > 0 ? '✓' : '○'} Country (at least one)</li>
                <li style={{ color: selectedIndicator ? '#10B981' : '#EF4444' }}>{selectedIndicator ? '✓' : '○'} Indicator</li>
              </ul>
            </div>
          ) : <div ref={chartRef} style={{ width: '100%', height: '100%' }} />}
        </div>
      </div>

      {showData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }} onClick={toggleData}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '90vw', maxHeight: '80vh', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Filtered Data ({filteredData.length} records)</h3>
              <button onClick={toggleData} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280' }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6' }}>
                  <tr>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Country')}>Country</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left' }}>ISO Code</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left' }}>Indicator</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Year')}>Year</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('UpperBound')}>Upper Bound</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Mean')}>Mean</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('LowerBound')}>Lower Bound</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Country}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.ISOCode}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB', fontSize: '12px' }}>{row.Indicator}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Year}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.UpperBound.toFixed(2)}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Mean.toFixed(2)}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.LowerBound.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <Tooltip id="info-tooltip" place="right" />
    </div>
  );
};

export default HealthDataVisualizer;